"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

const LETTERS = ["A", "B", "C", "D"];

interface CodeSelectCardProps {
  data: {
    question: string;
    options: Array<{ code: string; language: string }>;
    correctIndex: number;
    explanation: string;
  };
  onAnswer: (correct: boolean) => void;
}

export function CodeSelectCard({ data, onAnswer }: CodeSelectCardProps) {
  const { question, options, correctIndex, explanation } = data;
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);

  function handleSelect(index: number) {
    if (submitted) return;
    setSelectedIndex(index);
  }

  function handleCheck() {
    if (selectedIndex === null) return;
    setSubmitted(true);
    onAnswer(selectedIndex === correctIndex);
  }

  function getTileClasses(index: number) {
    if (submitted) {
      if (index === correctIndex) {
        return "border-green-500 bg-green-50";
      }
      if (index === selectedIndex && index !== correctIndex) {
        return "border-red-500 bg-red-50";
      }
      return "border-border opacity-60";
    }

    if (index === selectedIndex) {
      return "border-primary ring-2 ring-primary/20";
    }

    return "border-border hover:border-primary/50";
  }

  return (
    <div className="flex flex-col gap-6">
      <p className="text-lg font-medium mb-4">{question}</p>

      <div className="flex flex-col gap-4">
        {options.map((option, index) => (
          <button
            key={index}
            type="button"
            disabled={submitted}
            onClick={() => handleSelect(index)}
            className={cn(
              "rounded-lg border-2 p-1 text-left transition",
              submitted ? "cursor-default" : "cursor-pointer",
              getTileClasses(index)
            )}
          >
            <div className="text-sm font-semibold px-2 py-1">
              {LETTERS[index]}
            </div>
            <div className="bg-zinc-900 text-zinc-100 rounded-md p-3 text-base font-mono overflow-x-auto whitespace-pre">
              {option.code}
            </div>
          </button>
        ))}
      </div>

      {submitted && (
        <div className="bg-muted rounded-lg p-4 mt-4">
          <p className="font-medium mb-1">Explanation</p>
          <p className="text-sm leading-relaxed">{explanation}</p>
        </div>
      )}

      {!submitted && (
        <Button
          className="w-full"
          disabled={selectedIndex === null}
          onClick={handleCheck}
        >
          Check
        </Button>
      )}
    </div>
  );
}
